/**
 * What the garden shows while the player chooses where something goes: the
 * suggested spots glowing, and a red marker on a spot that won't do, with red
 * rings at the trees it would crowd. Drawn over the finished scene, whatever
 * the art, at the tiles' anchors (bottom centre).
 */
import type { PlantingProblem } from '@thicket/core';
import { anchorOf } from './maps.js';

/** What the scene shows while the player chooses where something goes. */
export interface PlantingAids {
  /** Suggested spots that could take it, glowing. */
  glow: [number, number][];
  /** The tile under the pointer, or the one just tapped, and what's wrong with it if anything. */
  at: { c: number; r: number; problem: PlantingProblem | null } | null;
  /** Bases of the trees that tile is too close to, ringed in red. */
  crowding: { x: number; y: number }[];
}

/** Rows of a small pixel ellipse lying on the ground, widest in the middle. */
const POOL = [7, 11, 13, 11, 7];
const CORE = [3, 7, 3];

function pool(ctx: CanvasRenderingContext2D, x: number, y: number, rows: number[], colour: string, alpha: number) {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = colour;
  const top = y - Math.floor(rows.length / 2);
  rows.forEach((w, i) => ctx.fillRect(x - Math.floor(w / 2), top + i, w, 1));
}

function ring(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, colour: string, alpha: number) {
  // A pixel ellipse outline w wide and 5 tall, so it reads at the zoomed-out view.
  ctx.globalAlpha = alpha;
  ctx.fillStyle = colour;
  const h = Math.floor(w / 2);
  ctx.fillRect(x - h + 3, y - 2, w - 6, 1);
  ctx.fillRect(x - h + 3, y + 2, w - 6, 1);
  ctx.fillRect(x - h + 1, y - 1, 2, 1);
  ctx.fillRect(x + h - 2, y - 1, 2, 1);
  ctx.fillRect(x - h + 1, y + 1, 2, 1);
  ctx.fillRect(x + h - 2, y + 1, 2, 1);
  ctx.fillRect(x - h, y, 1, 1);
  ctx.fillRect(x + h, y, 1, 1);
}

/** A pixel cross standing on the ground: "not here". */
function cross(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.globalAlpha = 1;
  const at = (dx: number, dy: number, c: string) => {
    ctx.fillStyle = c;
    ctx.fillRect(x + dx, y + dy, 1, 1);
  };
  for (let i = -4; i <= 4; i++) {
    for (const [dx, dy] of [[i - 1, i], [i + 1, i], [i, i - 1], [i, i + 1], [i - 1, -i], [i + 1, -i], [i, -i - 1], [i, -i + 1]] as const) at(dx, dy, '#5a120c');
  }
  for (let i = -4; i <= 4; i++) {
    at(i, i, '#ff5a46');
    at(i, -i, '#ff5a46');
  }
  at(0, 0, '#ffd2c8');
}

/**
 * Glowing suggested spots, and the red "too close" marker. Drawn over the
 * scene at the tiles' anchors (bottom centre), pulsing unless motion is reduced.
 */
export function drawPlantingAids(ctx: CanvasRenderingContext2D, aids: PlantingAids, frame: { tick: number; reduce: boolean }) {
  ctx.save();
  for (const [c, r] of aids.glow) {
    const { x, y } = anchorOf(c, r);
    const phase = ((c * 7 + r * 13) % 10) / 10;
    const beat = frame.reduce ? 0.5 : 0.5 + 0.5 * Math.sin((frame.tick / 6 + phase) * Math.PI * 2 / 2);
    pool(ctx, x, y, POOL, '#ffd94a', 0.4 + 0.3 * beat);
    pool(ctx, x, y, CORE, '#fffbe6', 0.6 + 0.4 * beat);
    ring(ctx, x, y, 17, '#fff1a0', 0.6 + 0.4 * beat);
    // A twinkle over the spot, so it reads from the whole-garden view.
    const tw = frame.reduce ? 1 : beat > 0.5 ? 2 : 1;
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = '#fffbe6';
    ctx.fillRect(x - tw, y - 8, tw * 2 + 1, 1);
    ctx.fillRect(x, y - 8 - tw, 1, tw * 2 + 1);
    if (!frame.reduce) {
      // Two motes drifting up from the spot.
      for (const k of [0, 1]) {
        const t = (frame.tick + Math.round(phase * 12) + k * 6) % 12;
        ctx.globalAlpha = 0.9 * (1 - t / 12);
        ctx.fillStyle = '#fffbe6';
        ctx.fillRect(x - 4 + k * 8 + ((t >> 2) % 2), y - 2 - t, 1, 1);
      }
    }
  }
  const at = aids.at;
  if (at) {
    const { x, y } = anchorOf(at.c, at.r);
    if (at.problem) {
      for (const t of aids.crowding) {
        pool(ctx, t.x, t.y, POOL, '#ff5a46', 0.35);
        ring(ctx, t.x, t.y, 21, '#ff5a46', 0.95);
      }
      pool(ctx, x, y, POOL, '#ff5a46', 0.55);
      ring(ctx, x, y, 17, '#ff5a46', 0.95);
      cross(ctx, x, y - 7);
    } else {
      pool(ctx, x, y, POOL, '#fffbe6', 0.7);
      ring(ctx, x, y, 17, '#ffffff', 1);
    }
  }
  ctx.restore();
}
